import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usd } from "@/lib/pricing";
import { TrendingUp, CalendarCheck, Car as CarIcon, DollarSign } from "lucide-react";

export const Route = createFileRoute("/admin/analytics")({
  head: () => ({ meta: [{ title: "Admin · Analytics" }] }),
  component: AdminAnalytics,
});

type Booking = {
  id: string;
  car_id: string;
  start_date: string;
  end_date: string;
  days: number;
  total: number;
  amount_paid: number | null;
  status: string;
  created_at: string;
};

type Car = { id: string; make: string; model: string; year: number };

type Range = 7 | 30 | 90 | 365;

function AdminAnalytics() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<Range>(30);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [b, c] = await Promise.all([
        supabase.from("bookings").select("id, car_id, start_date, end_date, days, total, amount_paid, status, created_at"),
        supabase.from("cars").select("id, make, model, year"),
      ]);
      setBookings((b.data ?? []) as Booking[]);
      setCars((c.data ?? []) as Car[]);
      setLoading(false);
    })();
  }, []);

  const carMap = useMemo(() => Object.fromEntries(cars.map(c => [c.id, c])), [cars]);

  const stats = useMemo(() => {
    const since = new Date();
    since.setDate(since.getDate() - range);
    const inRange = bookings.filter(b => new Date(b.created_at) >= since);
    const approved = inRange.filter(b => b.status === "approved");

    const revenue = approved.reduce((sum, b) => sum + Number(b.amount_paid ?? b.total ?? 0), 0);
    const totalBookings = inRange.length;
    const approvedCount = approved.length;
    const pendingCount = inRange.filter(b => b.status === "pending").length;

    // Occupancy: approved booked days / (cars * range days)
    const bookedDays = approved.reduce((sum, b) => sum + (b.days ?? 0), 0);
    const capacity = cars.length * range;
    const occupancy = capacity > 0 ? (bookedDays / capacity) * 100 : 0;

    // Top cars
    const carCounts: Record<string, { count: number; revenue: number }> = {};
    for (const b of approved) {
      const k = b.car_id;
      if (!carCounts[k]) carCounts[k] = { count: 0, revenue: 0 };
      carCounts[k].count += 1;
      carCounts[k].revenue += Number(b.amount_paid ?? b.total ?? 0);
    }
    const topCars = Object.entries(carCounts)
      .map(([id, v]) => ({ id, ...v, car: carMap[id] }))
      .filter(x => x.car)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return { revenue, totalBookings, approvedCount, pendingCount, occupancy, bookedDays, topCars };
  }, [bookings, cars, carMap, range]);

  if (loading) return <div className="text-espresso/60">Loading analytics…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-espresso">Analytics</h1>
        <div className="flex gap-2">
          {([7, 30, 90, 365] as Range[]).map(r => (
            <Button key={r} size="sm" variant={range === r ? "default" : "outline"} onClick={() => setRange(r)}>
              {r === 365 ? "1y" : `${r}d`}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={DollarSign} label="Revenue" value={usd(stats.revenue)} hint={`Last ${range} days · approved`} />
        <StatCard icon={CalendarCheck} label="Bookings" value={String(stats.totalBookings)} hint={`${stats.approvedCount} approved · ${stats.pendingCount} pending`} />
        <StatCard icon={TrendingUp} label="Occupancy" value={`${stats.occupancy.toFixed(1)}%`} hint={`${stats.bookedDays} booked car-days`} />
        <StatCard icon={CarIcon} label="Fleet" value={String(cars.length)} hint="Active cars" />
      </div>

      <Card className="p-6">
        <h2 className="mb-4 font-display text-xl text-espresso">Top booked cars</h2>
        {stats.topCars.length === 0 ? (
          <p className="text-espresso/60">No approved bookings in this period.</p>
        ) : (
          <div className="space-y-3">
            {stats.topCars.map((c, i) => (
              <div key={c.id} className="flex items-center justify-between border-b border-border pb-3 last:border-0 last:pb-0">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-sm font-semibold text-accent">{i + 1}</span>
                  <div>
                    <div className="font-medium text-espresso">{c.car.year} {c.car.make} {c.car.model}</div>
                    <div className="text-xs text-espresso/60">{c.count} booking{c.count > 1 ? "s" : ""}</div>
                  </div>
                </div>
                <div className="text-right font-medium text-espresso">{usd(c.revenue)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, hint }: { icon: any; label: string; value: string; hint: string }) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-espresso/60">{label}</div>
          <div className="mt-1 font-display text-2xl text-espresso">{value}</div>
        </div>
        <Icon className="h-5 w-5 text-accent" />
      </div>
      <div className="mt-2 text-xs text-espresso/50">{hint}</div>
    </Card>
  );
}
